/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React from 'react';
import { Link, RouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router';
import moment, { unitOfTime } from 'moment';
import qs from 'qs';

import { ROUTES, MONITOR_PAGE_URLS } from '../../../Common/constants/constants';

import { AlertTableValues } from 'src/Common/types/AlertsTypes';

interface InstanceDrilldownRow extends AlertTableValues {
  /** the entityTypeName is added to the row */
  entityTypeName: string;
}
export interface InstanceDrilldownLinkType extends RouteComponentProps<{ search: string }> {
  /** the unique id of the dashboard to build the link for */
  value: string;
  /** the full row values are optional */
  row: InstanceDrilldownRow;
  /** how far to offset the endDate in the link from the records date */
  endDateOffset: unitOfTime.DurationConstructor;
  /** optional title to show instead of the link value */
  title: string;
  /** show in new tab */
  shouldShowInNewTab: boolean;
}

/**
 * This component returns a link to an instance dashboard for a given entity
 * It is used in both the Alerts Table in our main UI, and in our Table Cards in our Summary to Instance Dashboards
 */
const InstanceDrilldownLink = ({
  value: entity,
  row,
  location,
  endDateOffset = 'minute',
  title = undefined,
  shouldShowInNewTab = false,
}: InstanceDrilldownLinkType): JSX.Element => {
  const { timestamp, entityTypeName } = row || {};

  const { endDate: summaryDashboardEndDate } = qs.parse(location.search?.replace('?', ''));

  const formattedEndDate = (
    timestamp // if the record itself has a timestamp, then use it
      ? moment(timestamp).add(1, endDateOffset)
      : moment(typeof summaryDashboardEndDate === 'string' ? summaryDashboardEndDate : undefined)
  ) // if we're matching the date from the summary dashboard, no offset should be used
    .format();

  const queryString = formattedEndDate
    ? `?${qs.stringify({
        endDate: formattedEndDate,
      })}`
    : '';

  const pathInstanceDashboard = `${ROUTES.MONITOR_DEVICE_TYPE}/${entityTypeName}/${MONITOR_PAGE_URLS.DEVICES}/${entity}${queryString}`;

  return (
    <Link
      className="bx--link"
      to={pathInstanceDashboard}
      target="_blank"
      rel="noopener noreferrer"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...(shouldShowInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
      {title ?? entity}
    </Link>
  );
};

export default withRouter(InstanceDrilldownLink);
